import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, User, ArrowLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useInstitutionMembershipMutations } from "../hooks";
import {
  searchMinisters,
  searchInstitutionMembers,
  createInstitutionMember,
  uploadMemberPhoto,
} from "@/Services/institutions.service";
import { useAuth } from "@/context/authContext";
import { MemberForm, defaultMemberFormValues } from "./MemberForm";
import type { MemberFormValues } from "./MemberForm";
import type { MinisterSearchResult } from "@/Services/institutions.service";
import type { InstitutionMember } from "../types";

type PersonTab = "minister" | "member";
type Mode = "search" | "create";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  institutionId: string;
}

function PersonRow({ name, subtitle, onClick }: { name: string | null; subtitle?: string | null; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <User className="size-5 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name ?? "—"}</p>
        {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </button>
  );
}

export function AddMemberDialog({ isOpen, onClose, institutionId }: Props) {
  const { user } = useAuth();
  const { add } = useInstitutionMembershipMutations(institutionId);

  const [personTab, setPersonTab] = useState<PersonTab>("minister");
  const [mode, setMode] = useState<Mode>("search");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [ministers, setMinisters] = useState<MinisterSearchResult[]>([]);
  const [members, setMembers] = useState<InstitutionMember[]>([]);
  const [formValues, setFormValues] = useState<MemberFormValues>(defaultMemberFormValues());

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const runSearch = useCallback(
    (q: string) => {
      setSearching(true);
      const fn = personTab === "minister" ? searchMinisters(q) : searchInstitutionMembers(q);
      fn.then((res) => {
        if (personTab === "minister") setMinisters(res as MinisterSearchResult[]);
        else setMembers(res as InstitutionMember[]);
      }).finally(() => setSearching(false));
    },
    [personTab],
  );

  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(search), 280);
    return () => clearTimeout(debounceRef.current);
  }, [search, personTab, isOpen, runSearch]);

  useEffect(() => {
    if (!isOpen) return;
    setPersonTab("minister");
    setMode("search");
    setSearch("");
    setMinisters([]);
    setMembers([]);
    setFormValues(defaultMemberFormValues());
  }, [isOpen]);

  async function handleSelectMinister(m: MinisterSearchResult) {
    setSaving(true);
    try { await add({ ministerId: m.id }); onClose(); }
    finally { setSaving(false); }
  }

  async function handleSelectMember(m: InstitutionMember) {
    setSaving(true);
    try { await add({ institutionMemberId: m.id }); onClose(); }
    finally { setSaving(false); }
  }

  async function handleCreateMember() {
    if (!formValues.first_name.trim() || !formValues.last_name.trim() || !user) return;
    setSaving(true);
    try {
      const created = await createInstitutionMember(
        {
          first_name: formValues.first_name.trim(),
          last_name: formValues.last_name.trim(),
          second_last_name: formValues.second_last_name.trim() || null,
          gender: formValues.gender || null,
          dni: formValues.dni.trim() || null,
          birth_date: formValues.birth_date || null,
          email: formValues.email.trim() || null,
          cell_phone: formValues.cell_phone.trim() || null,
          district_id: formValues.district_id || null,
          region_id: formValues.region_id || null,
        },
        user.id,
      );
      if (formValues.photoFile) {
        const filename = await uploadMemberPhoto(created.id, formValues.photoFile);
        await (await import("@/Services/institutions.service")).updateInstitutionMember(
          created.id,
          { profile_picture_filename: filename } as never,
          user.id,
        );
      }
      await add({ institutionMemberId: created.id });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const canCreate = formValues.first_name.trim().length > 0 && formValues.last_name.trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !saving && !o && onClose()}>
      <DialogContent className="flex max-h-[88vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? (
              <button
                onClick={() => setMode("search")}
                className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="size-4" />
                Nuevo miembro
              </button>
            ) : "Agregar miembro"}
          </DialogTitle>
        </DialogHeader>

        {mode === "search" ? (
          <div className="flex-1 space-y-4 overflow-y-auto py-1">
            <div className="flex gap-1 rounded-md border border-border p-1">
              {(["minister", "member"] as PersonTab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => { setPersonTab(tab); setSearch(""); }}
                  className={`flex-1 rounded px-3 py-1 text-xs font-medium transition-colors ${
                    personTab === tab ? "bg-primary text-primary-foreground" : "hover:bg-accent"
                  }`}
                >
                  {tab === "minister" ? "Ministro registrado" : "Miembro de institución"}
                </button>
              ))}
            </div>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o código…"
              disabled={saving}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />

            <div className="max-h-64 overflow-y-auto rounded-md border border-border">
              {searching ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              ) : personTab === "minister" ? (
                ministers.length === 0 ? (
                  <p className="py-6 text-center text-xs text-muted-foreground">
                    {search ? "Sin resultados" : "Escribe para buscar"}
                  </p>
                ) : ministers.map((m) => (
                  <PersonRow key={m.id} name={m.full_name} subtitle={m.adb_code} onClick={() => handleSelectMinister(m)} />
                ))
              ) : (
                <>
                  {members.length === 0 ? (
                    <p className="py-6 text-center text-xs text-muted-foreground">
                      {search ? "Sin resultados" : "Escribe para buscar"}
                    </p>
                  ) : members.map((m) => (
                    <PersonRow key={m.id} name={m.full_name} subtitle={m.dni ?? null} onClick={() => handleSelectMember(m)} />
                  ))}
                  <button
                    onClick={() => setMode("create")}
                    className="flex w-full items-center justify-center gap-2 border-t border-border px-3 py-3 text-xs font-medium text-primary hover:bg-accent"
                  >
                    + Crear nuevo miembro
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto py-1">
            <MemberForm values={formValues} onChange={setFormValues} disabled={saving} />
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          {mode === "create" && (
            <Button onClick={handleCreateMember} disabled={saving || !canCreate}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Crear y agregar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
